import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardHeader, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { orderAPI, OrderDetails } from '../../services/subscriptionApi';
import PaymentForm from '../../components/subscription/PaymentForm';
import StatusMessage, { PaymentStatus } from '../../components/subscription/StatusMessage';

// ─── Constants ────────────────────────────────────────────────────────────────

const UPI_ID = 'payments@company.upi';

const BANK_DETAILS = {
  accountName: 'Company Pvt Ltd',
  accountNumber: '1234567890',
  ifscCode: 'BANK0001234',
  bankName: 'Example Bank',
  branch: 'Main Branch',
};

// ─── Props ────────────────────────────────────────────────────────────────────

interface OrderPageProps {
  orderId?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

const OrderPage = ({ orderId: propOrderId }: OrderPageProps) => {
  const { orderId: paramOrderId } = useParams<{ orderId: string }>();
  const orderId = propOrderId ?? paramOrderId ?? '';

  const [order, setOrder] = useState<OrderDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // paymentSubmitted state is set by PaymentForm on successful submission
  const [paymentSubmitted, setPaymentSubmitted] = useState(false);
  // paymentStatus tracks the current status to display in StatusMessage
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>('pending');

  // Fetch order on mount
  useEffect(() => {
    if (!orderId) {
      setError('No order ID provided.');
      setLoading(false);
      return;
    }

    const fetchOrder = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await orderAPI.getOrderById(orderId);
        setOrder(response.data);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to load order details.';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId]);

  const handleProceedToPayment = () => {
    const paymentForm = document.getElementById('payment-form');
    if (paymentForm) {
      paymentForm.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // ── Loading state ──────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <LoadingSpinner size="xl" />
          <p className="mt-4 text-gray-600">Loading order details...</p>
        </div>
      </div>
    );
  }

  // ── Error state ────────────────────────────────────────────────────────────

  if (error || !order) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center">
        <div className="text-red-500 text-5xl mb-4">⚠️</div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Unable to load order</h2>
        <p className="text-gray-600">{error ?? 'Order not found.'}</p>
      </div>
    );
  }

  // ── Main render ────────────────────────────────────────────────────────────

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">

      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Complete Your Payment</h1>
        <p className="text-gray-600 mt-1">Follow the instructions below to complete your subscription.</p>
      </div>

      {/* ── Task 14.2: Order Details ─────────────────────────────────────── */}
      <Card>
        <CardHeader title="Order Summary" />
        <CardContent>
          <div className="space-y-3">
            {/* Order ID */}
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-sm font-medium text-gray-500">Order ID</span>
              <span className="text-sm font-mono font-semibold text-gray-900 bg-gray-100 px-2 py-1 rounded">
                {order.orderId}
              </span>
            </div>

            {/* Plan Name */}
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-sm font-medium text-gray-500">Plan</span>
              <span className="text-sm font-semibold text-gray-900">{order.planName}</span>
            </div>

            {/* Billing Duration */}
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-sm font-medium text-gray-500">Billing Duration</span>
              <span className="text-sm text-gray-900">{order.billingDuration}</span>
            </div>

            {/* Amount */}
            <div className="flex items-center justify-between py-2">
              <span className="text-sm font-medium text-gray-500">Amount</span>
              <span className="text-lg font-bold text-gray-900">₹{order.amount.toLocaleString('en-IN')}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Task 14.3: Payment Instructions ─────────────────────────────── */}
      <Card>
        <CardHeader
          title="Payment Instructions"
          subtitle="Make an offline payment using one of the methods below, then submit your transaction details."
        />
        <CardContent>
          <p className="text-sm text-gray-700 mb-4">
            Please transfer exactly{' '}
            <span className="font-bold text-gray-900">₹{order.amount.toLocaleString('en-IN')}</span>{' '}
            using any of the payment methods listed below. After completing the transfer, scroll down
            to submit your transaction ID for verification.
          </p>

          <div className="grid gap-4 sm:grid-cols-2">

            {/* UPI Method */}
            <Card variant="elevated" padding="sm">
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="text-xl">📱</span>
                  <h3 className="font-semibold text-gray-900">UPI Transfer</h3>
                </div>
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-xs text-blue-600 font-medium uppercase tracking-wide mb-1">UPI ID</p>
                  <p className="font-mono font-semibold text-blue-900 text-sm break-all">{UPI_ID}</p>
                </div>
                <p className="text-xs text-gray-500">
                  Open any UPI app (GPay, PhonePe, Paytm) and send{' '}
                  <span className="font-semibold">₹{order.amount.toLocaleString('en-IN')}</span> to the UPI ID above.
                </p>
              </div>
            </Card>

            {/* Bank Transfer Method */}
            <Card variant="elevated" padding="sm">
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="text-xl">🏦</span>
                  <h3 className="font-semibold text-gray-900">Bank Transfer</h3>
                </div>
                <div className="bg-green-50 rounded-lg p-3 space-y-1.5">
                  <div>
                    <p className="text-xs text-green-600 font-medium uppercase tracking-wide">Account Name</p>
                    <p className="text-sm font-semibold text-green-900">{BANK_DETAILS.accountName}</p>
                  </div>
                  <div>
                    <p className="text-xs text-green-600 font-medium uppercase tracking-wide">Account Number</p>
                    <p className="font-mono text-sm font-semibold text-green-900">{BANK_DETAILS.accountNumber}</p>
                  </div>
                  <div>
                    <p className="text-xs text-green-600 font-medium uppercase tracking-wide">IFSC Code</p>
                    <p className="font-mono text-sm font-semibold text-green-900">{BANK_DETAILS.ifscCode}</p>
                  </div>
                  <div>
                    <p className="text-xs text-green-600 font-medium uppercase tracking-wide">Bank</p>
                    <p className="text-sm text-green-900">{BANK_DETAILS.bankName} — {BANK_DETAILS.branch}</p>
                  </div>
                </div>
                <p className="text-xs text-gray-500">
                  Use NEFT/IMPS/RTGS to transfer{' '}
                  <span className="font-semibold">₹{order.amount.toLocaleString('en-IN')}</span>. Save the UTR number.
                </p>
              </div>
            </Card>

          </div>

          {/* Exact amount reminder */}
          <div className="mt-4 flex items-start gap-2 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <span className="text-yellow-500 mt-0.5">⚠️</span>
            <p className="text-sm text-yellow-800">
              Transfer the <span className="font-bold">exact amount of ₹{order.amount.toLocaleString('en-IN')}</span>.
              Mismatched amounts may delay verification.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* ── Task 14.4: Proceed to Payment button ─────────────────────────── */}
      {!paymentSubmitted && (
        <div className="flex justify-center">
          <Button
            variant="primary"
            size="lg"
            onClick={handleProceedToPayment}
          >
            Proceed to Payment
          </Button>
        </div>
      )}

      {/* ── Task 19.3: Status message after payment submission ───────────── */}
      {paymentSubmitted && (
        <StatusMessage status={paymentStatus} />
      )}

      {/* Payment form (Task 15) */}
      <div id="payment-form">
        <PaymentForm
          orderId={order.orderId}
          amount={order.amount}
          onSuccess={() => {
            setPaymentSubmitted(true);
            setPaymentStatus('pending');
          }}
        />
      </div>

    </div>
  );
};

export default OrderPage;
